import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, AlertTriangle } from "lucide-react";

export default function AdminLogin() {
  const { adminLogin, error, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const stale = params.get("stale") === "1";
  const nav = useNavigate();

  // Redirect already-signed-in admins from this page.
  useEffect(() => {
    if (user && user.role === "admin") nav("/admin", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await adminLogin(email, password);
    setLoading(false);
    if (ok) nav("/admin");
  };

  return (
    <div className="min-h-screen bg-[#0F1020] text-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/admin/login" className="flex items-center gap-3 justify-center mb-8">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center">
            <ShieldCheck size={22} className="text-[#0F1020]" />
          </div>
          <div>
            <div className="font-display font-bold text-white text-lg">Breathe·Thrive</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mt-0.5">Admin Console</div>
          </div>
        </Link>
        <form onSubmit={onSubmit} className="bg-[#151737] border border-white/10 rounded-3xl p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
          <h1 className="font-display text-2xl font-bold text-white">Staff sign-in</h1>
          <p className="text-sm text-slate-400 mt-1">Restricted to Breathe·Thrive team members.</p>
          {stale && (
            <div data-testid="admin-login-stale" className="mt-4 flex items-start gap-2 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>Your admin session expired (or was replaced by a parent login in another tab). Sign in again to keep editing.</div>
            </div>
          )}
          <div className="mt-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Email</label>
              <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl bg-[#0F1020] border border-white/10 focus:outline-none focus:border-amber-300 text-slate-100" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Password</label>
              <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl bg-[#0F1020] border border-white/10 focus:outline-none focus:border-amber-300 text-slate-100" />
            </div>
            {error && <div data-testid="admin-login-error" className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="admin-login-submit" disabled={loading} className="w-full bg-gradient-to-r from-amber-400 to-rose-400 text-[#0F1020] px-6 py-3 rounded-full font-semibold hover:opacity-90 disabled:opacity-50">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </div>
        </form>
        <div className="mt-4 text-center text-xs text-slate-500">
          Parent looking for the family app? <Link to="/login" className="text-slate-300 underline">Sign in here</Link>.
        </div>
      </div>
    </div>
  );
}
